import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import {
  UserPlusIcon, TrashIcon, PencilIcon, CheckIcon, XMarkIcon,
  BuildingStorefrontIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';

/**
 * The people behind each counter.
 *
 * A store login is a branch, not a person — Ramlet and Downtown each have one
 * account shared across shifts — so a manual activation used to be recorded
 * against "Ramlet El Bayda Till", which names the terminal and nobody at all.
 *
 * The manager keeps a short list per branch here, and the cashier picks from
 * it. A dropdown rather than a text field on purpose: a name typed at a busy
 * counter gets spelled three ways and eventually left blank, and this is the
 * one path where a membership is granted on somebody's word.
 */
export default function StoreStaffPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Adding: which branch is open, and the name being typed.
  const [addingTo, setAddingTo] = useState(null);
  const [newName, setNewName]   = useState('');
  const [newCode, setNewCode]   = useState('');
  const [busy, setBusy]         = useState(false);

  // Renaming one person in place.
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.getStoreStaff()
      .then(r => setBranches(r.branches || []))
      .catch(e => setError(e.message || 'Could not load the counter staff.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (adminId) => {
    if (!newName.trim()) return;
    setBusy(true); setError('');
    try {
      await adminAPI.addStoreStaff({
        adminId, name: newName.trim(), staffCode: newCode.trim() || undefined,
      });
      setNewName(''); setNewCode(''); setAddingTo(null);
      load();
    } catch (e) {
      setError(e.message || 'Could not add them.');
    } finally { setBusy(false); }
  };

  const handleRename = async (id) => {
    if (!editName.trim()) return setEditId(null);
    setBusy(true); setError('');
    try {
      await adminAPI.updateStoreStaff(id, { name: editName.trim() });
      setEditId(null);
      load();
    } catch (e) {
      setError(e.message || 'Could not rename them.');
    } finally { setBusy(false); }
  };

  const handleToggle = async (m) => {
    setBusy(true); setError('');
    try {
      await adminAPI.updateStoreStaff(m.id, { isActive: !m.isActive });
      load();
    } catch (e) {
      setError(e.message || 'Could not update them.');
    } finally { setBusy(false); }
  };

  const handleRemove = async (m) => {
    // Someone who has granted memberships is retired, not erased — their past
    // activations have to stay attributable. The confirm says which it will be
    // so the manager is not surprised to find the name still listed.
    const msg = m.activations > 0
      ? `${m.name} has made ${m.activations} activation${m.activations === 1 ? '' : 's'}.\n\n`
        + 'They will be removed from the counter dropdown but kept on those records.'
      : `Remove ${m.name} from the list?`;
    if (!window.confirm(msg)) return;

    setBusy(true); setError('');
    try {
      await adminAPI.deleteStoreStaff(m.id);
      load();
    } catch (e) {
      setError(e.message || 'Could not remove them.');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Counter Staff</h1>
          <p>
            Who can be named on a membership activated at the counter. The PIN
            is how they sign in at the till — give each person their own and
            their name lands on everything they do. Leave it blank and they
            share the house PIN, which names nobody.
          </p>
        </div>
        <div className="page-header-actions">
          <button className="btn-ghost" onClick={load}>
            <ArrowPathIcon style={{ width:15, height:15 }} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding:14, marginBottom:16, color:'#B91C1C' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="card" style={{ padding:20 }}>
          <div className="skeleton" style={{ height:18, width:220, borderRadius:6 }} />
        </div>
      ) : branches.length === 0 ? (
        <div className="card" style={{ padding:20 }}>
          <div className="empty-state">
            <BuildingStorefrontIcon style={{ width:28, height:28 }} />
            <h3>No counter accounts</h3>
            <p>Create a store login first, then add the people who use it.</p>
          </div>
        </div>
      ) : branches.map(b => (
        <div key={b.adminId} className="card" style={{ padding:0, marginBottom:16, overflow:'hidden' }}>
          <div style={{
            padding:'14px 20px', borderBottom:'1px solid var(--border)',
            display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
          }}>
            <BuildingStorefrontIcon style={{ width:17, height:17, color:'var(--text-muted)' }} />
            <strong>{b.branch || b.email}</strong>
            <span style={{ color:'var(--text-muted)', fontSize:12 }}>{b.email}</span>
            <button
              className="btn-ghost"
              style={{ marginLeft:'auto' }}
              onClick={() => { setAddingTo(addingTo === b.adminId ? null : b.adminId); setNewName(''); setNewCode(''); }}
            >
              <UserPlusIcon style={{ width:15, height:15 }} /> Add someone
            </button>
          </div>

          {addingTo === b.adminId && (
            <div style={{
              padding:'14px 20px', borderBottom:'1px solid var(--border)',
              display:'flex', gap:10, flexWrap:'wrap', alignItems:'center',
            }}>
              <input
                autoFocus
                placeholder="Full name"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd(b.adminId)}
                style={{ flex:'2 1 200px', minWidth:0, padding:'9px 12px',
                         border:'1px solid var(--border)', borderRadius:8, fontSize:15 }}
              />
              <input
                placeholder="PIN they sign in with"
                value={newCode}
                onChange={e => setNewCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd(b.adminId)}
                style={{ flex:'1 1 150px', minWidth:0, padding:'9px 12px',
                         border:'1px solid var(--border)', borderRadius:8, fontSize:15 }}
              />
              <button className="btn-primary" disabled={busy} onClick={() => handleAdd(b.adminId)}>
                Add
              </button>
              <button className="btn-ghost" onClick={() => setAddingTo(null)}>Cancel</button>
            </div>
          )}

          {b.staff.length === 0 ? (
            <div style={{ padding:'18px 20px', color:'var(--text-muted)', fontSize:13 }}>
              Nobody on this counter yet. Until somebody is added, activations here are
              recorded against the branch rather than a person.
            </div>
          ) : (
            <div className="table-wrap" style={{ border:'none', borderRadius:0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Code</th><th>Activations</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {b.staff.map(m => (
                    <tr key={m.id} style={{ opacity: m.isActive ? 1 : 0.5 }}>
                      <td>
                        {editId === m.id ? (
                          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                            <input
                              autoFocus
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleRename(m.id);
                                if (e.key === 'Escape') setEditId(null);
                              }}
                              style={{ padding:'6px 10px', border:'1px solid var(--border)',
                                       borderRadius:6, fontSize:14, minWidth:0 }}
                            />
                            <button className="btn-icon" onClick={() => handleRename(m.id)} title="Save">
                              <CheckIcon style={{ width:15, height:15 }} />
                            </button>
                            <button className="btn-icon" onClick={() => setEditId(null)} title="Cancel">
                              <XMarkIcon style={{ width:15, height:15 }} />
                            </button>
                          </div>
                        ) : (
                          <>
                          <span style={{ fontWeight:600 }}>{m.name}</span>
                          {/* A dashboard login tied to this person. That
                              account sees only this name at the counter and
                              cannot record an activation under anybody
                              else's -- worth showing, because it is
                              invisible otherwise and somebody will wonder
                              why their list has one entry. */}
                          {m.ownerEmail && (
                            <span
                              title={`${m.ownerEmail} can only act as ${m.name}`}
                              style={{ marginLeft:8, fontSize:11, background:'#EFF6FF',
                                       color:'#1D4ED8', padding:'2px 8px', borderRadius:20 }}
                            >
                              {m.ownerEmail}
                            </span>
                          )}
                          </>
                        )}
                      </td>
                      <td style={{ fontFamily:'monospace', fontSize:12, color:'var(--text-muted)' }}>
                        {m.staffCode || '—'}
                      </td>
                      <td>{m.activations}</td>
                      <td>
                        {m.isActive
                          ? <span className="badge badge-active">On the list</span>
                          : <span className="badge badge-inactive">Retired</span>}
                      </td>
                      <td style={{ whiteSpace:'nowrap' }}>
                        <button
                          className="btn-icon"
                          title="Rename"
                          onClick={() => { setEditId(m.id); setEditName(m.name); }}
                        >
                          <PencilIcon style={{ width:15, height:15 }} />
                        </button>
                        <button
                          className="btn-ghost"
                          style={{ padding:'4px 10px', fontSize:12, marginLeft:6 }}
                          onClick={() => handleToggle(m)}
                          disabled={busy}
                        >
                          {m.isActive ? 'Retire' : 'Restore'}
                        </button>
                        <button
                          className="btn-icon"
                          title="Remove"
                          onClick={() => handleRemove(m)}
                          disabled={busy}
                        >
                          <TrashIcon style={{ width:15, height:15 }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
